import PageWrapper from "../../lib/page-wrapper";
import React, { useState, useEffect } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { getCampaignUUID, getShowUUID } from "../../lib/getters_setters";
import { useDispatch, useSelector } from "react-redux";
import { getShowCampaign, respondToCampaignOffer } from "../../../actions/campaigns";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import * as moment from "moment";
import { CONFIRMATION_MODAL } from "../../modals/modal_root";
import { withRouter } from "react-router-dom";
import AcceptAdvertisementModal from "./accept_advertisement_modal";
import { showModal } from "../../../actions/modal";
import { formatMoney } from "../../../lib/format-money";
import { getEstimatedImpressionsForShow } from "../../../lib/campaigns";
import { getShow } from "../../../action_managers/shows";
import "./shows.scss";
import { useComponentDidMount } from "../../../lib/hook";
import { If } from "react-extras";
import {
  CampaignDetailsSummaryPageInfo,
  SummaryPageInfo,
  SummaryPageSectionWrapper,
} from "../../lib/summary-page-sections/summary-page-sections";
import {
  CampaignItemStateAccepted,
  CampaignItemStateAwaitingAudio,
  CampaignItemStateCompleted,
  CampaignItemStateDeclined,
  CampaignItemStateExpired,
  CampaignItemStateRunning,
  CampaignStyleHostRead,
} from "../../../constants/campaigns";
import ProductInstructionsInfo from "./product_instructions_info";
import MediaPlayer from "../../lib/media_player";
import { showWarning } from "../../../actions/app";
import { redemptionCodeValueToDisplay } from "../../modals/campaign_editor/campaign_editor_helpers";

// should be false, but set to true for debugging
const defaultShowAcceptModal = false;
const defaultShowAwaitingModal = false;

const campaignStyleToDisplayString = {
  hostRead: "Podcast Host-Read Ad",
  campaignAudio: "Pre-Recorded Ad",
};

const targetingOptionsToPlacements = (targetingOptions = {}) => {
  let placements = [];

  if (targetingOptions.preRoll) {
    placements.push("Pre-Roll");
  }
  if (targetingOptions.midRoll) {
    placements.push("Mid-Roll");
  }
  if (targetingOptions.postRoll) {
    placements.push("Post-Roll");
  }

  return placements.join(", ");
};

const showMediaPlayerStates = new Set([
  CampaignItemStateCompleted,
  CampaignItemStateRunning,
  CampaignItemStateAccepted,
]);

const ShowCampaignPage = props => {
  const { history } = props;

  const [showAcceptModal, setShowModal] = useState(defaultShowAcceptModal);
  const [showAwaitingModal, setShowAwaitingModal] = useState(defaultShowAwaitingModal);

  const campaignUUID = getCampaignUUID(props);
  const showUUID = getShowUUID(props);
  const show = useSelector(state => state.shows.shows[showUUID]);

  const dispatch = useDispatch();
  const campaignByUUID = useSelector(state => state.campaigns.campaignByUUID);
  const campaign = get(campaignByUUID, campaignUUID, {});
  const campaignItemByUUID = useSelector(state => state.campaignItems.campaignItemByUUID);
  const showCampaignItemUUIDs = useSelector(state => state.campaignItems.showCampaignItemUUIDs);
  const campaignItems = get(showCampaignItemUUIDs, showUUID, []).map(
    uuid => campaignItemByUUID[uuid]
  );
  const campaignItem = find(campaignItems, item => {
    return item.showUUID === showUUID && item.campaignUUID === campaignUUID;
  });
  useEffect(() => {
    if (!campaignItem || campaignItem.freshState) return;
    if (campaignItem.state === CampaignItemStateExpired) {
      dispatch(
        showWarning(
          "Unfortunately, the response time for this campaign has expired and your show is no longer eligible to participate."
        )
      );
      history.push(`/shows/${showUUID}/advertising`);
    }

    if (campaignItem.state === CampaignItemStateDeclined) {
      dispatch(
        showWarning(
          "Unfortunately, you previously declined this offer and your show is no longer eligible to participate in this campaign."
        )
      );
      history.push(`/shows/${showUUID}/advertising`);
    }
  }, [campaignItem?.state]);

  useComponentDidMount(() => {
    if (isEmpty(campaign)) {
      dispatch(getShowCampaign(showUUID, campaignUUID));
    }
    if (isEmpty(show)) {
      dispatch(getShow(showUUID));
    }
  }, []);

  const notInterestedConfirmation = () => {
    return dispatch(
      showModal(CONFIRMATION_MODAL, {
        modalTitle: "Are you sure?",
        confirmationMessage:
          "Once you decline this offer, you will not be able to change your response for this campaign.",
        actionButtonName: "I'm Sure",
        callback: () => {
          dispatch(respondToCampaignOffer(campaignItem.uuid, "declined")).then(resp => {
            const statusCode = get(resp, "status");
            if (statusCode === 200) {
              history.push(`/shows/${showUUID}/advertising`);
            }
          });
        },
      })
    );
  };

  if (!campaignItem) return null;
  const advertisementSettings = campaignItem?.budget?.advertisementSettings ?? {};
  const impressions = getEstimatedImpressionsForShow(
    campaign,
    { advertisementSettings },
    campaignItem?.totalBudget ?? 0
  );

  // TODO see if there is code reusability with the campaign stuff
  const estimatedWeeklyDownloads = show?.estimatedWeeklyDownloads ?? 1;
  const estimatedWeeks = impressions / estimatedWeeklyDownloads;
  const estimatedWeeksHumanized = moment.duration(estimatedWeeks, "weeks").humanize();
  const startAt = moment.unix(campaignItem.startAt);
  const endAt = startAt.clone().add(estimatedWeeks, "weeks");

  const showMediaPlayer = showMediaPlayerStates.has(campaignItem.state);
  const mediaFileUUID =
    campaign.style === CampaignStyleHostRead
      ? campaignItem.mediaFileUUID
      : campaign.contentMediaFileUUID;
  return (
    <PageWrapper backLink={`/shows/${showUUID}/advertising`} title="Ad Details">
      <ProductInstructionsInfo campaignItem={campaignItem} campaign={campaign} show={show} />
      <SummaryPageSectionWrapper title={"Campaign Details"}>
        <CampaignDetailsSummaryPageInfo campaign={campaign} />
      </SummaryPageSectionWrapper>
      <SummaryPageSectionWrapper title={"Campaign Style"}>
        <SummaryPageInfo title={"Type of Ad"} text={campaignStyleToDisplayString[campaign.style]} />
        <SummaryPageInfo
          title={"Ad Placement"}
          text={targetingOptionsToPlacements(campaign.targetingOptions)}
        />
        <SummaryPageInfo
          title={"Estimated Timeline"}
          text={`${startAt.format("LL")} – ${endAt.format("LL")}`}
        />
        We estimate that this campaign will run for about {estimatedWeeksHumanized} based on your
        CPM rates and show size.
      </SummaryPageSectionWrapper>
      <SummaryPageSectionWrapper title={"Product Samples"}>
        <SummaryPageInfo
          noColon
          title={"Free product samples?"}
          text={redemptionCodeValueToDisplay[campaign.productExchangeType]}
          newline
        />
        <If condition={campaign.productExchangeType === campaign.productExchangeInstructions}>
          <>
            <SummaryPageInfo
              title={"Redemption Instructions*"}
              text={campaign.productExchangeInstructions}
              newline
            />
            {campaign.redemptionCodes && (
              <SummaryPageInfo
                title={"Number of Redemption Codes Uploaded*"}
                text={Object.keys(campaign.redemptionCodes).length}
              />
            )}
          </>
        </If>
      </SummaryPageSectionWrapper>
      <SummaryPageSectionWrapper title={"Payment"}>
        <div className="flex-row-container">
          <div className={"m-rm"}>
            <div className="bold">Total Payment:</div>
            <div>
              Your final earnings based on your chosen CPMs, show size, and RedCircle's commission.
              Payment schedule will be incremental and occurs once the advertiser completes payment,
              which can vary based on if the campaign is paid up front or invoiced.
            </div>
          </div>
          <div className="campaign-total-payment bold">
            {formatMoney(campaignItem.totalCreatorAmount)}
          </div>
        </div>
      </SummaryPageSectionWrapper>
      <If condition={!!(showMediaPlayer && mediaFileUUID)}>
        <SummaryPageSectionWrapper title={"Advertisement Audio"}>
          <MediaPlayer mediaFileUUID={mediaFileUUID} />
        </SummaryPageSectionWrapper>
      </If>
      {campaignItem.state === "sent" && (
        <Row className="margin-top--large">
          <Col xs={12} md={6}>
            <Button className="btn btn-secondary width-100" onClick={notInterestedConfirmation}>
              Not Interested
            </Button>
          </Col>
          <Col xs={12} md={6}>
            <Button className="btn btn-primary width-100" onClick={() => setShowModal(true)}>
              Continue
            </Button>
          </Col>
        </Row>
      )}
      {campaignItem.state === CampaignItemStateAwaitingAudio && (
        <Row className="margin-top--large">
          <Col xs={12} mdOffset={3} md={6}>
            <Button
              className="btn btn-primary width-100"
              onClick={() => setShowAwaitingModal(true)}>
              Upload Audio
            </Button>
            Click “Upload Audio” to view the Host-Read script and upload your ad read.
          </Col>
        </Row>
      )}
      {showAwaitingModal && (
        <AcceptAdvertisementModal
          creatorAmount={campaignItem.totalCreatorAmount}
          campaign={campaign}
          campaignItem={campaignItem}
          show={show}
          hideModal={() => setShowAwaitingModal(false)}
          isAwaitingAudio
        />
      )}
      {showAcceptModal && (
        <AcceptAdvertisementModal
          creatorAmount={campaignItem.totalCreatorAmount}
          campaign={campaign}
          campaignItem={campaignItem}
          show={show}
          hideModal={() => setShowModal(false)}
        />
      )}
    </PageWrapper>
  );
};

export default withRouter(ShowCampaignPage);
